import { useState, useEffect, useRef } from 'react';

export default function useWindowDimensions() {
  const hasWindow = typeof window !== 'undefined';

  const isFirstRender = useRef(true);

  function getViewportDimensions() {
    if (!hasWindow) return { width: null, height: null, safeTop: 0, safeBottom: 0 };

    let width = window.innerWidth;
    let height = window.innerHeight;

    // Only use visualViewport for mobile / small screens
    if (window.visualViewport && window.innerWidth < 1024) {
      width = window.visualViewport.width;
      height = window.visualViewport.height;
    }

    const safeTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top')) || 0;
    const safeBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom')) || 0;

    return { width, height: height - safeTop - safeBottom, safeTop, safeBottom };
  }

  const [dimensions, setDimensions] = useState(getViewportDimensions);

  useEffect(() => {
    if (!hasWindow) return;

    let timeoutId;

    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newDims = getViewportDimensions();
        setDimensions(prev => {
          if (
            prev.width === newDims.width &&
            prev.height === newDims.height &&
            prev.safeTop === newDims.safeTop &&
            prev.safeBottom === newDims.safeBottom
          ) {
            return prev; // no change
          }
          return newDims;
        });
      }, 50);
    }

    window.addEventListener('resize', handleResize);

    // visualViewport only on mobile
    if (window.visualViewport && window.innerWidth < 1024) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    // Skip the second update on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setDimensions(getViewportDimensions());
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport && window.innerWidth < 1024) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
      clearTimeout(timeoutId);
    };
  }, [hasWindow]);

  return dimensions;
}
